import { FC, useEffect, useState } from 'react'
import { ColorOptions } from '../../theme/UiThemeProvider'
import { useBreakPointProps } from '../../utils/utils'
import { Box, BoxProps } from '../Box'
import { Loader } from '../Loader'
import { Text, TextProps } from '../Text'

interface ButtonProps {
  type?: keyof typeof buttonTypes | Array<keyof typeof buttonTypes>
  onPress: () => void | Promise<any> | Array<() => void | Promise<any>>
  isLoading?: boolean | Array<boolean>
  disabled?: boolean | Array<boolean>
  title?: string | Array<string>
  isRound?: boolean | Array<boolean>
  isFull?: boolean | Array<boolean>
  testID?: string
  height?: any
  accessibilityLabel?: string
  hide?: boolean
  color?: any
  textColor?: any
  children?: any
  underlayColor?: ColorOptions | Array<ColorOptions>
}

const defaultStyles = {
  text: () =>
    ({
      color: 'WHITE',
    } as TextProps),
  container: (isRound: boolean, isFull: boolean, height: any) =>
    ({
      height: height ? height : 'MEDIUM_BIG',
      width: isFull ? undefined : 'HUGE',
      justifyContent: 'center',
      alignSelf: isFull ? 'stretch' : undefined,
      alignItems: 'center',
      backgroundColor: 'PRIMARY',
      borderRadius: isRound ? 24 : 'MEDIUM',
      underlayColor: 'PRIMARY_STORNG',
    } as BoxProps),
  disabledOpacity: 0.5,
}

const buttonTypes = {
  primary: {
    ...defaultStyles,
  },
  secondary: {
    ...defaultStyles,
    container: (isRound: boolean, isFull: boolean, height: any) =>
      ({
        height: height ? height : 'MEDIUM_2',
        width: isFull ? undefined : 'HUGE',
        justifyContent: 'center',
        alignSelf: isFull ? 'stretch' : undefined,
        alignItems: 'center',
        borderRadius: isRound ? 24 : 'MEDIUM',
        underlayColor: 'PRIMARY',
        borderColor: 'PRIMARY',
        borderWidth: 'NORMAL',
        backgroundColor: 'WHITE',
      } as BoxProps),
    text: (isPressed: boolean) =>
      ({
        color: isPressed ? 'WHITE' : 'PRIMARY',
      } as TextProps),
  },
  SecondaryWithBg: {
    ...defaultStyles,
    container: () =>
      ({
        backgroundColor: 'BLUE',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 3,
        flexDirection: 'row',
        paddingHorizontal: 12,
        paddingVertical: 6,
        shadowRadius: 1,
        shadowColor: 'BLACK',
      } as BoxProps),
    text: () =>
      ({
        color: 'WHITE',
        fontSize: 12,
        fontWeight: '600',
      } as TextProps),
  },
  IconLH: {
    ...defaultStyles,
    container: () =>
      ({
        backgroundColor: 'WHITE',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 3,
        flexDirection: 'row',
        paddingHorizontal: 12,
        paddingVertical: 6,
        shadowRadius: 1,
        shadowColor: 'BLACK',
      } as BoxProps),

    text: () =>
      ({
        color: 'BLUE',
        fontSize: 12,
        fontWeight: '600',
        textAlign: 'center',
      } as TextProps),
  },
}

export const Button: FC<ButtonProps> = (props) => {
  const { children, testID, accessibilityLabel } = props
  const modifiedProps = useBreakPointProps<ButtonProps>(props)
  const {
    type = 'primary',
    isLoading: propsLoading = false,
    onPress,
    title,
    isRound = false,
    isFull = false,
    disabled = false,
    height,
    hide = false,
    color,
    textColor,
    underlayColor = 'PRIMARY_STORNG',
  } = modifiedProps
  const [pressState, setPressState] = useState(false)
  const [isLoading, setLoading] = useState(false)

  useEffect(() => {
    setLoading(propsLoading as boolean)
  }, [propsLoading])
  const onPressInternal = async () => {
    if (isLoading || disabled) {
      return
    }
    try {
      setLoading(true)
      if (onPress) {
        await onPress()
      }
      setLoading(false)
    } catch (ex) {
      setLoading(false)
      throw ex
    }
  }

  if (hide) {
    return null
  }

  const buttonStyle = buttonTypes[type as keyof typeof buttonTypes].container(
    isRound as boolean,
    isFull as boolean,
    height,
  )
  if (color) {
    buttonStyle.backgroundColor = color
  }

  const textStyle = buttonTypes[type as keyof typeof buttonTypes].text(pressState)
  if (textColor) {
    textStyle.color = textColor
  }

  const contents = children ? (
    children
  ) : (
    <>
      <Text {...textStyle}>{title}</Text>
    </>
  )

  return (
    <Box
      testID={testID}
      accessibilityLabel={accessibilityLabel}
      disabled={disabled}
      onPress={onPressInternal}
      onPressIn={() => setPressState(true)}
      onPressOut={() => setPressState(false)}
      opacity={disabled ? buttonTypes[type as keyof typeof buttonTypes].disabledOpacity : 1}
      {...buttonStyle}
      underlayColor={underlayColor}>
      {isLoading ? <Loader color="WHITE" /> : contents}
    </Box>
  )
}
